// Shared per-(project, kind) mode-document store for decomposed panels
// (docs/editor-consolidation/panel-content-spec.md §3.1).
//
// useModeDocument (../useModeDocument.ts) is built around one mounted
// consumer per (projectId, kind): one load, one DebouncedSaver, one
// flush-on-unmount. That is wrong the moment a single mode's content is
// split into sibling panels that all edit the same kind - N independent
// loads, N independent debounce timers each scheduling saves off their own
// possibly-stale copy, and any one panel closing flushes/tears down state
// the others still depend on.
//
// This module owns exactly one load and one DebouncedSaver per (projectId,
// kind), reference-counted across every panel currently mounted for that
// key. useSharedModeDocument's call shape matches useModeDocument's
// ({data, setData, flush, loading, error}) so a panel body barely changes -
// only where the load/save lifecycle lives moves up a level.
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { toast } from '../../design/toast';
import { useEditorHost } from '../EditorHost';
import { loadModeDocument, saveModeDocument, type ModeDocKind } from '../../services/docs';
import { createDebouncedSaver, type DebouncedSaver } from '../debouncedSave';
import type { ModeDocState } from '../useModeDocument';

const SAVE_DEBOUNCE_MS = 1500;

const MODE_LABELS: Record<ModeDocKind, string> = {
  logic: 'Logic',
  hud: 'HUD',
  audio: 'Audio',
  fx: 'FX',
  gamedata: 'Game Data',
  pixel: 'Pixel Studio',
  model: 'Model Studio',
  gamesetup: 'Game Setup',
  prefabs: 'Prefabs',
  cutscenes: 'Cutscenes',
  introsequences: 'Intro Sequences',
};

interface Entry<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refCount: number;
  touched: boolean;
  saver: DebouncedSaver<T>;
  listeners: Set<() => void>;
}

// Module-level singleton, keyed by "projectId:kind" - deliberately not
// scoped to a React tree, since sibling panels under different dockview
// groups (and thus different subtrees) both need to reach the same entry.
const entries = new Map<string, Entry<unknown>>();

function keyFor(projectId: string, kind: ModeDocKind): string {
  return projectId + ':' + kind;
}

function notify<T>(entry: Entry<T>): void {
  entry.listeners.forEach((l) => l());
}

function ensureEntry<T extends Record<string, unknown>>(projectId: string, kind: ModeDocKind, fallback: T): Entry<T> {
  const key = keyFor(projectId, kind);
  const existing = entries.get(key) as Entry<T> | undefined;
  if (existing) return existing;

  const label = MODE_LABELS[kind] ?? kind;
  const entry: Entry<T> = {
    data: fallback,
    loading: true,
    error: null,
    refCount: 0,
    touched: false,
    listeners: new Set(),
    saver: createDebouncedSaver<T>((value, { keepalive }) => {
      saveModeDocument(projectId, kind, value, { keepalive })
        .then(() => {
          entry.error = null;
          notify(entry);
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'Save failed.';
          entry.error = message;
          notify(entry);
          toast(`${label} didn't save: ${message}`, 'error');
        });
    }, SAVE_DEBOUNCE_MS),
  };
  entries.set(key, entry);

  loadModeDocument<T>(projectId, kind)
    .then((doc) => {
      // An edit that arrived before this load resolved must win - the
      // response is stale by the time it lands (same rule useModeDocument
      // applies per-consumer; here it's per-entry, shared by every panel).
      if (entry.touched) return;
      const stored = doc.data && Object.keys(doc.data).length > 0 ? doc.data : fallback;
      entry.data = stored;
      entry.loading = false;
      notify(entry);
    })
    .catch(() => {
      entry.loading = false;
      notify(entry);
      toast(`Couldn't load your saved ${label} data - you're starting from a blank ${label} and a save here may overwrite what was stored. Reload once you're back online.`, 'error');
    });

  return entry;
}

function acquire<T extends Record<string, unknown>>(projectId: string, kind: ModeDocKind, fallback: T): Entry<T> {
  const entry = ensureEntry(projectId, kind, fallback);
  entry.refCount += 1;
  return entry;
}

/** Releases this consumer's hold on the entry; the last release flushes any
 * pending save and tears the entry down (the next acquire starts fresh). */
function release(projectId: string, kind: ModeDocKind): void {
  const key = keyFor(projectId, kind);
  const entry = entries.get(key);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    entry.saver.flush();
    entries.delete(key);
  }
}

function getEntry<T>(projectId: string, kind: ModeDocKind): Entry<T> | undefined {
  return entries.get(keyFor(projectId, kind)) as Entry<T> | undefined;
}

/**
 * Shared replacement for useModeDocument: any number of panels calling this
 * with the same (projectId, kind) share one load and one debounced save.
 * `fallback` should be referentially stable (a module-level constant, as
 * every existing mode already uses) - only the first acquirer's fallback is
 * used per entry.
 */
export function useSharedModeDocument<T extends Record<string, unknown>>(kind: ModeDocKind, fallback: T): ModeDocState<T> {
  const { projectId } = useEditorHost();
  const fallbackRef = useRef(fallback);
  const [, forceRender] = useReducer((c: number) => c + 1, 0);

  useEffect(() => {
    const entry = acquire<T>(projectId, kind, fallbackRef.current);
    entry.listeners.add(forceRender);
    // Pick up whatever the entry already holds (it may have loaded, or even
    // been edited, before this consumer mounted).
    forceRender();
    return () => {
      entry.listeners.delete(forceRender);
      release(projectId, kind);
    };
  }, [projectId, kind]);

  const setData = useCallback(
    (next: T | ((current: T) => T)) => {
      const entry = getEntry<T>(projectId, kind);
      if (!entry) return;
      const resolved = typeof next === 'function' ? (next as (current: T) => T)(entry.data) : next;
      entry.touched = true;
      entry.data = resolved;
      notify(entry);
      entry.saver.schedule(resolved);
    },
    [projectId, kind],
  );

  const flush = useCallback(() => {
    getEntry<T>(projectId, kind)?.saver.flush();
  }, [projectId, kind]);

  const entry = getEntry<T>(projectId, kind);
  return {
    data: entry?.data ?? fallback,
    setData,
    flush,
    loading: entry?.loading ?? true,
    error: entry?.error ?? null,
  };
}
